import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Where a person is in their companion panel, for one brand.
 *
 * The key is the person AND the brand, never the brand alone: two colleagues
 * share a browser profile far less often than they share a brand, and a panel
 * that remembered "card 2 of 3" across accounts would show one of them the card
 * the other was reading. Switching brand reads another key, so nothing leaks
 * sideways either.
 */

export type CompanionPanelScope = string;

/** How the conversational panel sits beside the page (see `decidePanelMode`). */
export type PanelMode = 'push' | 'overlay' | 'fullscreen';

/** The scope key. One place, so no caller can invent a different shape. */
export const companionPanelScope = (userId: string, brandId: string): CompanionPanelScope =>
  `${userId}:${brandId}`;

type CompanionScopeState = {
  open: boolean;
  index: number;
  whyOpenFor: string | null;
};

const emptyScopeState = (): CompanionScopeState => ({ open: false, index: 0, whyOpenFor: null });

/** What the email call to action hands the composer when it opens it. */
export type CompanionEmailCtaRequest = {
  actionId: string;
  affiliateUserId: string;
  programId: string;
  recipientName: string | null;
  /**
   * The message the card came with, or null when this card already seeded it
   * once. A second opening never writes over what the brand has been typing.
   */
  draft: { subject: string; body: string } | null;
};

/**
 * What the invitation call to action hands the wizard when it opens it.
 *
 * `collab` opens on the collaboration-type step, the way the contact card's
 * "propose" entry does; `invite` opens straight on the programme step, on the
 * one the card named when it named one. Both only ever open — nothing is sent
 * from here.
 */
export type CompanionInvitationCtaRequest = {
  actionId: string;
  mode: 'invite' | 'collab';
  socialProfileId: string;
  creatorIdentityId: string | null;
  /** The programme the card named, or null when the wizard should ask. */
  programId: string | null;
};

/**
 * What the renewal call to action hands the display rights composer.
 *
 * The composer resolves the gesture from the server, so the card only names the
 * authorization and the conditions it would start from: the same amount, for
 * one month. It seeds a NEW chain and never touches a version already in play,
 * so a draft the brand has begun editing keeps what they typed.
 */
export type CompanionDisplayRightsCtaRequest = {
  actionId: string;
  authorizationId: string;
  /** Personalises the composer's default message; null when unknown. */
  creatorName: string | null;
  initialTerms: { amountCents: number; durationMonths: number };
};

/** Immutable targets carried by one expired-gift card into its review dialog. */
export type CompanionGiftReofferCtaRequest = {
  actionId: string;
  version: number;
  giftRequestIds: string[];
};

type CompanionPanelStore = {
  byScope: Record<CompanionPanelScope, CompanionScopeState>;
  /** The composer the panel asked for, or null while none is open. */
  emailCta: CompanionEmailCtaRequest | null;
  /** The invitation wizard the panel asked for, or null while none is open. */
  invitationCta: CompanionInvitationCtaRequest | null;
  /** The display rights composer the panel asked for, or null while none is open. */
  displayRightsCta: CompanionDisplayRightsCtaRequest | null;
  giftReofferCta: CompanionGiftReofferCtaRequest | null;
  /**
   * Cards whose draft has already been handed to the composer. Never
   * persisted: a reload empties the composer too, so remembering across
   * sessions would leave a card unable to offer its draft again.
   */
  draftSeededActionIds: string[];
  /**
   * Cards this browsing session already reported as seen, per scope. Never
   * persisted: `seen` is an exposure, and a new session is a new exposure. The
   * server dedups per person anyway, so this only spares a redundant call.
   */
  sessionSeen: Record<CompanionPanelScope, Set<string>>;
  /** The visible-card list the stored index was computed against, per scope. */
  visibleKeyByScope: Record<CompanionPanelScope, string>;
  /**
   * How the conversational panel sits beside the page: decided at opening,
   * then moved only when the window crosses one of the thresholds
   * (`decidePanelMode`), so an ordinary resize never reflows the page under the
   * reader. Never persisted: a panel restored open without a mode counts as
   * closed. The floating feed panel opens without one and ignores it.
   */
  panelMode: PanelMode | null;
  /** Moves the mode of a panel already open; a closed panel keeps none. */
  setPanelMode: (mode: PanelMode) => void;
  openPanel: (scope: CompanionPanelScope, mode?: PanelMode) => void;
  closePanel: (scope: CompanionPanelScope) => void;
  /** With a mode, a panel restored open without one is opened, not closed. */
  togglePanel: (scope: CompanionPanelScope, modeIfOpening?: PanelMode) => void;
  setIndex: (scope: CompanionPanelScope, index: number) => void;
  setWhyOpenFor: (scope: CompanionPanelScope, actionId: string | null) => void;
  /** Sends the reader back to the first card when the visible cards changed. */
  syncVisibleIds: (scope: CompanionPanelScope, ids: string[]) => void;
  /** True the first time this session sees that card in that scope. */
  markSeenLocally: (scope: CompanionPanelScope, actionId: string) => boolean;
  /** Opens the composer for a card, seeding its draft at most once. */
  openEmailCta: (
    request: Omit<CompanionEmailCtaRequest, 'draft'> & {
      draft: { subject: string; body: string };
    }
  ) => void;
  closeEmailCta: () => void;
  /** Opens the invitation wizard for a card, on the step its mode names. */
  openInvitationCta: (request: CompanionInvitationCtaRequest) => void;
  closeInvitationCta: () => void;
  /** Opens the display rights composer for a card, on its own authorization. */
  openDisplayRightsCta: (request: CompanionDisplayRightsCtaRequest) => void;
  closeDisplayRightsCta: () => void;
  openGiftReofferCta: (request: CompanionGiftReofferCtaRequest) => void;
  closeGiftReofferCta: () => void;
};

export const useCompanionPanelStore = create<CompanionPanelStore>()(
  devtools(
    persist(
      (set, get) => {
        const patchScope = (
          scope: CompanionPanelScope,
          patch: Partial<CompanionScopeState>
        ): void => {
          set((state) => ({
            byScope: {
              ...state.byScope,
              [scope]: { ...(state.byScope[scope] ?? emptyScopeState()), ...patch },
            },
          }));
        };

        const isOpen = (scope: CompanionPanelScope, withMode: boolean): boolean =>
          (get().byScope[scope]?.open ?? false) && (!withMode || get().panelMode !== null);

        const openPanel = (scope: CompanionPanelScope, mode?: PanelMode): void => {
          if (mode && !isOpen(scope, true)) set({ panelMode: mode });
          patchScope(scope, { open: true });
        };

        const closePanel = (scope: CompanionPanelScope): void => {
          set({ panelMode: null });
          patchScope(scope, { open: false });
        };

        return {
          byScope: {},
          sessionSeen: {},
          visibleKeyByScope: {},
          panelMode: null,
          emailCta: null,
          invitationCta: null,
          displayRightsCta: null,
          giftReofferCta: null,
          draftSeededActionIds: [],

          openPanel,
          closePanel,
          setPanelMode: (mode) => {
            const current = get().panelMode;
            if (current !== null && current !== mode) set({ panelMode: mode });
          },
          togglePanel: (scope, modeIfOpening) =>
            isOpen(scope, modeIfOpening !== undefined)
              ? closePanel(scope)
              : openPanel(scope, modeIfOpening),
          setIndex: (scope, index) => patchScope(scope, { index: Math.max(0, index) }),
          setWhyOpenFor: (scope, actionId) => patchScope(scope, { whyOpenFor: actionId }),

          syncVisibleIds: (scope, ids) => {
            const key = ids.join('|');
            if (get().visibleKeyByScope[scope] === key) return;

            set((state) => ({
              visibleKeyByScope: { ...state.visibleKeyByScope, [scope]: key },
              byScope: {
                ...state.byScope,
                [scope]: { ...(state.byScope[scope] ?? emptyScopeState()), index: 0 },
              },
            }));
          },

          markSeenLocally: (scope, actionId) => {
            const seen = get().sessionSeen[scope];
            if (seen?.has(actionId)) return false;

            set((state) => ({
              sessionSeen: {
                ...state.sessionSeen,
                [scope]: new Set(state.sessionSeen[scope] ?? []).add(actionId),
              },
            }));
            return true;
          },

          openEmailCta: (request) => {
            const seeded = get().draftSeededActionIds.includes(request.actionId);

            set((state) => ({
              emailCta: { ...request, draft: seeded ? null : request.draft },
              draftSeededActionIds: seeded
                ? state.draftSeededActionIds
                : [...state.draftSeededActionIds, request.actionId],
            }));
          },

          closeEmailCta: () => set({ emailCta: null }),

          openInvitationCta: (request) => set({ invitationCta: request }),
          closeInvitationCta: () => set({ invitationCta: null }),

          openDisplayRightsCta: (request) => set({ displayRightsCta: request }),
          closeDisplayRightsCta: () => set({ displayRightsCta: null }),
          openGiftReofferCta: (request) => set({ giftReofferCta: request }),
          closeGiftReofferCta: () => set({ giftReofferCta: null }),
        };
      },
      {
        name: 'companion-panel-storage',
        partialize: (state) => ({ byScope: state.byScope }),
      }
    ),
    { name: 'CompanionPanelStore' }
  )
);
